/**
 * SignalR client for the Envy.Messaging WidgetHub.
 *
 * Connects to /hubs/widget with the visitor session JWT.
 * Receives real-time messages, status changes, and typing indicators.
 * Sends typing indicators, delivery acknowledgments, and read receipts.
 *
 * Replaces the 5-second polling mechanism with push-based updates.
 * Falls back to polling gracefully if the connection fails.
 */
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import type { WidgetMessageEvent } from '../types';

export interface WidgetHubCallbacks {
  onNewMessage: (message: WidgetMessageEvent) => void;
  onStatusChanged: (conversationId: string, newStatus: string) => void;
  onTypingIndicator: (conversationId: string, senderName: string, isTyping: boolean) => void;
  onConnectionChanged: (connected: boolean) => void;
}

export class WidgetSignalRClient {
  private connection: HubConnection | null = null;
  private hubUrl: string;
  private tokenFactory: () => string | null;
  private callbacks: WidgetHubCallbacks;
  private currentConversationId: string | null = null;
  private isDisposed = false;

  constructor(
    apiUrl: string,
    tokenFactory: () => string | null,
    callbacks: WidgetHubCallbacks
  ) {
    // Derive hub URL from API URL (same host, different path)
    this.hubUrl = apiUrl.replace(/\/$/, '') + '/hubs/widget';
    this.tokenFactory = tokenFactory;
    this.callbacks = callbacks;
  }

  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  async connect(): Promise<void> {
    if (this.isDisposed) return;
    if (this.connection?.state === HubConnectionState.Connected ||
        this.connection?.state === HubConnectionState.Connecting) return;

    const token = this.tokenFactory();
    if (!token) {
      console.debug('[envy-chat] No session token, skipping SignalR connect');
      return;
    }

    try {
      this.connection = new HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          accessTokenFactory: () => this.tokenFactory() || '',
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (ctx) => {
            // Give up after 5 minutes
            if (ctx.elapsedMilliseconds > 300_000) return null;
            // Exponential backoff capped at 30s with jitter
            return Math.min(1000 * Math.pow(2, ctx.previousRetryCount), 30_000)
              + Math.random() * 1000;
          },
        })
        .configureLogging(LogLevel.Warning)
        .build();

      this.registerHandlers();

      await this.connection.start();
      this.callbacks.onConnectionChanged(true);

      console.debug('[envy-chat] SignalR connected to widget hub');

      // Re-join conversation group if we had one
      if (this.currentConversationId) {
        await this.joinConversation(this.currentConversationId);
      }
    } catch (err) {
      console.warn('[envy-chat] SignalR connection failed, will rely on polling:', err);
      this.callbacks.onConnectionChanged(false);
    }
  }

  private registerHandlers(): void {
    if (!this.connection) return;

    // Server → Client: New message from agent
    this.connection.on('NewMessage', (message: WidgetMessageEvent) => {
      this.callbacks.onNewMessage(message);
    });

    // Server → Client: Conversation status changed
    this.connection.on('ConversationStatusChanged', (conversationId: string, newStatus: string) => {
      this.callbacks.onStatusChanged(conversationId, newStatus);
    });

    // Server → Client: Agent typing indicator
    this.connection.on('TypingIndicator', (conversationId: string, senderName: string, isTyping: boolean) => {
      this.callbacks.onTypingIndicator(conversationId, senderName, isTyping);
    });

    // Connection state tracking
    this.connection.onreconnecting(() => {
      this.callbacks.onConnectionChanged(false);
    });

    this.connection.onreconnected(async () => {
      this.callbacks.onConnectionChanged(true);
      // Re-join conversation group after reconnect
      if (this.currentConversationId) {
        await this.joinConversation(this.currentConversationId);
      }
    });

    this.connection.onclose(() => {
      this.callbacks.onConnectionChanged(false);
    });
  }

  // === Client → Server invocations ===

  async joinConversation(conversationId: string): Promise<void> {
    this.currentConversationId = conversationId;
    if (!this.isConnected) return;

    try {
      await this.connection!.invoke('JoinConversation', conversationId);
      console.debug('[envy-chat] Joined conversation group:', conversationId);
    } catch (err) {
      console.warn('[envy-chat] Failed to join conversation group:', err);
    }
  }

  async leaveConversation(conversationId: string): Promise<void> {
    if (this.currentConversationId === conversationId) {
      this.currentConversationId = null;
    }
    if (!this.isConnected) return;

    try {
      await this.connection!.invoke('LeaveConversation', conversationId);
    } catch {
      // Silently ignore — connection may already be closed
    }
  }

  async sendTypingIndicator(conversationId: string, isTyping: boolean): Promise<void> {
    if (!this.isConnected) return;

    try {
      await this.connection!.invoke('SendTypingIndicator', conversationId, isTyping);
    } catch {
      // Typing indicators are fire-and-forget
    }
  }

  async acknowledgeDelivery(conversationId: string, messageId: string): Promise<void> {
    if (!this.isConnected) return;

    try {
      await this.connection!.invoke('AcknowledgeDelivery', conversationId, messageId);
    } catch {
      // Delivery receipts are fire-and-forget
    }
  }

  async markAsRead(conversationId: string, lastReadMessageId: string): Promise<void> {
    if (!this.isConnected) return;

    try {
      await this.connection!.invoke('MarkAsRead', conversationId, lastReadMessageId);
    } catch {
      // Read receipts are fire-and-forget
    }
  }

  async disconnect(): Promise<void> {
    this.isDisposed = true;
    if (this.currentConversationId) {
      await this.leaveConversation(this.currentConversationId);
    }
    try {
      await this.connection?.stop();
    } catch {
      // Ignore disconnect errors
    }
    this.connection = null;
  }
}
